import React, { useRef } from "react";
import {
  Box,
  Flex,
  HStack,
  Spacer,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Tooltip,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  VStack,
} from "@chakra-ui/react";
import { track, EVENTS } from "../../utils/analytics";
import { RiBarChartGroupedFill, RiCircleFill } from "react-icons/ri";
import { MdBook } from "react-icons/md";
import { BiGroup } from "react-icons/bi";
import { KnowledgeBaseHelperTopics, ToolbarFeatures } from "../../types";
import { useFilters } from "../../hooks/useFilters";
import KnowledgeBaseHelper from "../../features/knowledgebase/KnowledgeBaseHelper";
import PricingQueuePoll from "../pricing/PricingQueuePoll";
import Uploads from "../../features/uploads/Uploads";
import { ToolbarDropDown } from "../design_library/toolbar/ToolbarDropDown";
import { ToolbarButton } from "../design_library/toolbar/ToolbarButton";
import { ToolbarIconButton } from "../design_library/toolbar/ToolbarIconButton";
import CustomSwitch from "../design_library/toolbar/Switch";
import { FilterSettings } from "../../features/filter-settings/FilterSettings";
import { DashboardSelector } from "../../features/dashboard-selector/DashboardSelector";
import { TimeFrameSelector } from "../timeframe-selector/TimeFrameSelector";
import InsightsManager from "../../features/insights/InsightsManager/InsightsManager";
import { AutoSync } from "../../features/AutoSync/AutoSync";
import PageHeading from "../design_library/PageHeading";
import SidebarToggle from "./SidebarToggle";
import { FiMoreHorizontal } from "react-icons/fi";
import { useAtomValue } from "jotai";
import { ViewModeAtom } from "../../store";

export const Toolbar = ({
  enabledOptions,
  helpKey,
  positionsController,
  transactionsController,
  title,
  description,
  icon,
  onAutoAssignStrategies,
  autoAssignButtonText,
  extraActions,
}: {
  enabledOptions?: ToolbarFeatures[];
  helpKey?: KnowledgeBaseHelperTopics;
  positionsController?: any;
  transactionsController?: any;
  title?: string;
  description?: string;
  icon?: any;
  onAutoAssignStrategies?: () => void;
  autoAssignButtonText?: string;
  extraActions?: React.ReactNode;
}) => {
  const filtersHook = useFilters();
  const confirm = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const viewMode = useAtomValue(ViewModeAtom);

  const statusOptions = [
    { value: "all", label: "All positions" },
    { value: "active", label: "Active" },
    { value: "open", label: "Open positions only" },
    { value: "closed", label: "Closed positions only" },
  ];

  const groupingOptions = [
    { value: "B/U/P", label: "Book -> Underlying -> Positions" },
    { value: "U/B/P", label: "Underlying -> Book -> Positions" },
    { value: "B/P/U", label: "Book -> Positions -> Underlying" },
    { value: "Underlying Only", label: "Underlying Only" },
    { value: "None", label: "No grouping" },
  ];

  // Track toolbar feature usage
  const trackToolbarFeatureUsed = (feature: string, details?: any) => {
    track(EVENTS.TOOLBAR.TOOLBAR_FEATURE_USED, {
      feature,
      viewMode,
      ...(details || {}),
    });
  };

  // Vertical separator component for visual grouping
  const VerticalSeparator = () => (
    <Box
      h="24px"
      w="1px"
      bg="rgba(255, 255, 255, 0.1)"
      alignSelf="center"
      display={{ base: "none", md: "block" }}
    />
  );

  return (
    <Flex className="grid-toolbar-hook" mx="2" mb={2}>
      <HStack
        className="grid-toolbar-select"
        wrap={"wrap"}
        maxW="100vw"
        w="100%"
        spacing={2}
        overflowX="auto"
        overflowY="hidden"
      >
        <SidebarToggle />
        {/* {enabledOptions?.includes(ToolbarFeatures.TITLE) &&
          enabledOptions?.includes(ToolbarFeatures.DESCRIPTION) && (
            <PageHeading title={title || ""} description={description || ""} icon={icon} />
          )} */}

        {/* Context/Primary Controls */}
        {enabledOptions?.includes(ToolbarFeatures.DASHBOARD_SELECTOR) && (
          <Tooltip label="Select Dashboard" hasArrow placement="bottom">
            <Box onClick={() => trackToolbarFeatureUsed("dashboard_selector")}>
              <DashboardSelector />
            </Box>
          </Tooltip>
        )}

        {enabledOptions?.includes(ToolbarFeatures.TIMEFRAMES) && (
          <Tooltip label="Select Time Period" hasArrow placement="bottom">
            <Box onClick={() => trackToolbarFeatureUsed("timeframes")}>
              <TimeFrameSelector />
            </Box>
          </Tooltip>
        )}

        {(enabledOptions?.includes(ToolbarFeatures.DASHBOARD_SELECTOR) ||
          enabledOptions?.includes(ToolbarFeatures.TIMEFRAMES)) &&
          (enabledOptions?.includes(ToolbarFeatures.FILTER_SELECTOR) ||
            enabledOptions?.includes(ToolbarFeatures.GROUPING) ||
            enabledOptions?.includes(ToolbarFeatures.STATUS)) && (
            <VerticalSeparator />
          )}

        {/* Table-Level Controls */}
        {enabledOptions?.includes(ToolbarFeatures.FILTER_SELECTOR) &&
          viewMode === "advanced" && (
            <Tooltip label="Configure Filters" hasArrow placement="bottom">
              <Box onClick={() => trackToolbarFeatureUsed("filter_selector")}>
                <FilterSettings />
              </Box>
            </Tooltip>
          )}

        {enabledOptions?.includes(ToolbarFeatures.GROUPING) &&
          positionsController && (
            <Tooltip
              label="Change position grouping"
              hasArrow
              placement="bottom"
            >
              <Box>
                <ToolbarDropDown
                  className="tool-bar-select tutorial-grouping"
                  aria-label="Position grouping selector"
                  placeholder={
                    <HStack color="white !important">
                      <RiBarChartGroupedFill fill="var(--accent)" />
                      <Text
                        display={{
                          base: "none",
                          md: "block",
                        }}
                      >
                        Grouping
                      </Text>
                    </HStack>
                  }
                  defaultValue={{
                    label: groupingOptions.find(
                      (option) =>
                        option.value ===
                        localStorage.getItem("positionGrouping")
                    )?.label || (
                      <HStack color="white !important">
                        <RiBarChartGroupedFill fill="var(--accent)" />
                        <Text
                          display={{
                            base: "none",
                            md: "block",
                          }}
                          color="var(--white)"
                        >
                          Grouping
                        </Text>
                      </HStack>
                    ),
                  }}
                  value={
                    positionsController.grouping !== "B/U/P"
                      ? {
                          label: positionsController.grouping,
                          value: positionsController.grouping,
                        }
                      : {
                          label: (
                            <HStack color="white !important">
                              <RiBarChartGroupedFill fill="var(--accent)" />
                              <Text
                                display={{
                                  base: "none",
                                  md: "block",
                                }}
                                color="var(--white)"
                              >
                                Grouping
                              </Text>
                            </HStack>
                          ),
                          value: "None",
                        }
                  }
                  options={groupingOptions}
                  onChange={(event: any) => {
                    trackToolbarFeatureUsed("grouping", { value: event.value });
                    positionsController.selectGrouping(event.value);
                  }}
                />
              </Box>
            </Tooltip>
          )}

        {enabledOptions?.includes(ToolbarFeatures.STATUS) && (
          <Tooltip
            label="Filter by position status"
            hasArrow
            placement="bottom"
          >
            <Box>
              <ToolbarDropDown
                className="tool-bar-select"
                aria-label="Position status filter"
                placeholder={
                  <HStack color="white !important">
                    <RiCircleFill fill="var(--accent)" />
                    <Text
                      display={{
                        base: "none",
                        lg: "block",
                      }}
                    >
                      Status
                    </Text>
                  </HStack>
                }
                options={statusOptions}
                defaultValue={{
                  value: localStorage.getItem("positionStatus") || "all",
                  label: statusOptions.find(
                    (option) =>
                      option.value === localStorage.getItem("positionStatus")
                  )?.label || (
                    <HStack color="white !important">
                      <RiCircleFill fill="var(--accent)" />
                      <Text
                        display={{
                          base: "none",
                          lg: "block",
                        }}
                      >
                        Status
                      </Text>
                    </HStack>
                  ),
                }}
                onChange={(event: any) => {
                  track(EVENTS.POSITION.VIEW_CHANGED, {
                    positionView: event.value,
                  });
                  trackToolbarFeatureUsed("status", { value: event.value });

                  filtersHook.setPositionStatus(event.value);
                }}
              />
            </Box>
          </Tooltip>
        )}

        {enabledOptions?.includes(
          ToolbarFeatures.TOGGLE_UNBOOKED_AND_NO_POS_GROUP
        ) &&
          viewMode === "advanced" && (
            <>
              <Tooltip
                label="Show trades without a book assignment"
                hasArrow
                placement="bottom"
              >
                <Box>
                  <CustomSwitch
                    icon={<MdBook size={14} />}
                    id="isChecked"
                    isChecked={transactionsController.showUnBookedTrades}
                    onChange={(e: any) => {
                      trackToolbarFeatureUsed("toggle_unbooked", {
                        value: !transactionsController.showUnBookedTrades,
                      });
                      transactionsController.setShowUnBookedTrades(
                        !transactionsController.showUnBookedTrades
                      );
                    }}
                    mr={2}
                    className="custom-switch-with-elipsis"
                    aria-label="Toggle unbooked trades"
                  >
                    Unbooked ({transactionsController.tradesWithNoBook})
                  </CustomSwitch>
                </Box>
              </Tooltip>

              <Tooltip
                label="Show trades without position grouping"
                hasArrow
                placement="bottom"
              >
                <Box>
                  <CustomSwitch
                    icon={<BiGroup size={14} />}
                    id="isChecked"
                    className="custom-switch-with-elipsis"
                    isChecked={transactionsController.showNoPositionGrouping}
                    onChange={(e: any) => {
                      trackToolbarFeatureUsed("toggle_no_position_grouping", {
                        value: !transactionsController.showNoPositionGrouping,
                      });
                      transactionsController.setShowNoPositionGrouping(
                        !transactionsController.showNoPositionGrouping
                      );
                    }}
                    mr={2}
                    aria-label="Toggle no position grouping"
                  >
                    No position group (
                    {transactionsController.tradesWithNoPositionGrouping})
                  </CustomSwitch>
                </Box>
              </Tooltip>
            </>
          )}

        {/* commented out for now for perf reasons only */}
        {/* {enabledOptions?.includes(ToolbarFeatures.PRICING_QUEUE) && (
          <Tooltip label="Pricing queue status" hasArrow placement="bottom">
            <Box onClick={() => trackToolbarFeatureUsed("pricing_queue")}>
              <PricingQueuePoll />
            </Box>
          </Tooltip>
        )} */}

        <Spacer
          display={{
            base: "none",
            md: "block",
          }}
        />

        {/* Desktop actions */}
        <HStack display={{ base: "none", md: "flex" }} spacing={2}>
          {(enabledOptions?.includes(ToolbarFeatures.AUTOSYNC) ||
            enabledOptions?.includes(ToolbarFeatures.HELP) ||
            enabledOptions?.includes(ToolbarFeatures.INSIGHTS) ||
            extraActions) && <VerticalSeparator />}

          {enabledOptions?.includes(ToolbarFeatures.AUTOSYNC) && (
            <Tooltip
              label="Auto-sync portfolio data"
              hasArrow
              placement="bottom"
            >
              <Box onClick={() => trackToolbarFeatureUsed("autosync")}>
                <AutoSync minimalGui={true} />
              </Box>
            </Tooltip>
          )}

          {enabledOptions?.includes(ToolbarFeatures.INSIGHTS) && (
            <Tooltip label="AI Insights" hasArrow placement="bottom">
              <Box onClick={() => track(EVENTS.TOOLBAR.INSIGHT_BUTTON_CLICKED)}>
                <InsightsManager />
              </Box>
            </Tooltip>
          )}

          {enabledOptions?.includes(ToolbarFeatures.HELP) && (
            <Tooltip label="Help & Documentation" hasArrow placement="bottom">
              <Box
                onClick={() =>
                  track(EVENTS.TOOLBAR.HELP_OPENED, {
                    topic: helpKey ? String(helpKey) : "unknown",
                  })
                }
              >
                <KnowledgeBaseHelper topic={helpKey ? String(helpKey) : ""} />
              </Box>
            </Tooltip>
          )}

          {extraActions && <Box>{extraActions}</Box>}

          {onAutoAssignStrategies && (
            <>
              <Box display={{ base: "block", md: "none" }}>
                <Tooltip
                  label={
                    autoAssignButtonText ||
                    "Auto-assign strategies to position groups"
                  }
                  hasArrow
                  placement="bottom"
                >
                  <ToolbarIconButton
                    aria-label={
                      autoAssignButtonText ||
                      "Auto-assign strategies to position groups"
                    }
                    icon={<BiGroup />}
                    variant="outline"
                    onClick={confirm.onOpen}
                  />
                </Tooltip>
              </Box>
              <Box display={{ base: "none", md: "block" }}>
                <ToolbarButton
                  variant="outline"
                  leftIcon={<BiGroup />}
                  onClick={confirm.onOpen}
                  aria-label={
                    autoAssignButtonText ||
                    "Auto-assign strategies to position groups"
                  }
                >
                  Auto-assign
                </ToolbarButton>
              </Box>
              <AlertDialog
                isOpen={confirm.isOpen}
                leastDestructiveRef={cancelRef}
                onClose={confirm.onClose}
              >
                <AlertDialogOverlay>
                  <AlertDialogContent bg="var(--bg)" color="var(--white)">
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                      Confirm auto-assign
                    </AlertDialogHeader>
                    <AlertDialogBody>
                      This will replace all position groups with each position's
                      strategy. Do you want to continue?
                    </AlertDialogBody>
                    <AlertDialogFooter>
                      <ToolbarButton
                        ref={cancelRef}
                        onClick={confirm.onClose}
                        variant="ghost"
                      >
                        Cancel
                      </ToolbarButton>
                      <ToolbarButton
                        onClick={() => {
                          onAutoAssignStrategies();
                          confirm.onClose();
                        }}
                        ml={3}
                        variant="outline"
                        borderColor="var(--accent)"
                        color="var(--accent)"
                        _hover={{ bg: "rgba(255, 100, 100, 0.1)" }}
                      >
                        Yes, continue
                      </ToolbarButton>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialogOverlay>
              </AlertDialog>
            </>
          )}
        </HStack>

        {/* Keep Add assets visible at all sizes */}
        <Tooltip
          label="Upload transactions & documents"
          hasArrow
          placement="bottom"
        >
          <Box onClick={() => trackToolbarFeatureUsed("uploads")}>
            <Uploads />
          </Box>
        </Tooltip>

        {/* Mobile overflow menu */}
        <Box display={{ base: "block", md: "none" }}>
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <Tooltip label="More options" hasArrow placement="bottom">
                <ToolbarIconButton
                  aria-label="More options"
                  icon={<FiMoreHorizontal />}
                  variant="ghost"
                />
              </Tooltip>
            </PopoverTrigger>
            <PopoverContent
              p={3}
              bg="var(--bg)"
              borderColor="rgba(255, 255, 255, 0.18)"
              color="var(--white)"
              boxShadow="lg"
            >
              <PopoverBody>
                <VStack spacing={3} alignItems="stretch">
                  {enabledOptions?.includes(ToolbarFeatures.AUTOSYNC) && (
                    <Tooltip
                      label="Auto-sync portfolio data"
                      hasArrow
                      placement="left"
                    >
                      <Box onClick={() => trackToolbarFeatureUsed("autosync")}>
                        <AutoSync minimalGui={true} />
                      </Box>
                    </Tooltip>
                  )}
                  {enabledOptions?.includes(ToolbarFeatures.INSIGHTS) && (
                    <Tooltip label="AI Insights" hasArrow placement="left">
                      <Box
                        onClick={() =>
                          track(EVENTS.TOOLBAR.INSIGHT_BUTTON_CLICKED)
                        }
                      >
                        <InsightsManager />
                      </Box>
                    </Tooltip>
                  )}
                  {enabledOptions?.includes(ToolbarFeatures.HELP) && (
                    <Tooltip
                      label="Help & Documentation"
                      hasArrow
                      placement="left"
                    >
                      <Box
                        onClick={() =>
                          track(EVENTS.TOOLBAR.HELP_OPENED, {
                            topic: helpKey ? String(helpKey) : "unknown",
                          })
                        }
                      >
                        <KnowledgeBaseHelper
                          topic={helpKey ? String(helpKey) : ""}
                        />
                      </Box>
                    </Tooltip>
                  )}
                  {extraActions && (
                    <Box borderTop="1px solid rgba(255, 255, 255, 0.1)" pt={3}>
                      {extraActions}
                    </Box>
                  )}
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </Box>
      </HStack>
    </Flex>
  );
};
